import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'chart.js/auto';
import { Chart as ChartJS, ChartConfiguration, BubbleDataPoint } from 'chart.js';
import { ExternalLink } from 'lucide-react';
import { useCaseService } from '../../services/useCaseService';
import { domainService } from '../../services/domainService';
import { UseCaseSummary, Domain } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../Common/sheet';
import { getUseCaseStatusLabel } from '../UseCases/useCaseStatus';

// Define the expected data point structure
interface UseCaseMatrixDataPoint {
  id: string;
  name: string;
  x: number; // Value Score (axe X)
  y: number; // Complexity Score (axe Y)
  status?: string; // Current status, used in tooltips
  color?: string; // Optional color override
  domainId?: string | null; // Domain ID for filtering
}

interface ZoneGroup {
  x: number;
  y: number;
  items: UseCaseMatrixDataPoint[];
}

// Domain option for the selector
interface DomainOption {
  id: string;
  name: string;
}

interface UseCaseMatrixCardProps {
  title?: string;
  /** Optional: provide data externally instead of fetching */
  externalData?: UseCaseMatrixDataPoint[];
  /** Optional: provide domains externally instead of fetching */
  externalDomains?: DomainOption[];
  /** Optional: filter by domain ID */
  filterDomainId?: string;
  /** Optional: custom click handler, defaults to navigation */
  onUseCaseClick?: (id: string) => void;
  /** Optional: limit number of use cases to fetch */
  limit?: number;
}

// Define quadrant colors matching the image
const QUADRANT_COLORS = {
  topLeft: 'rgba(156, 163, 175, 0.3)',     // Gray - Faible valeur, Effort important
  topRight: 'rgba(251, 191, 36, 0.3)',     // Orange/Yellow - Valeur importante, Effort important
  bottomLeft: 'rgba(147, 197, 253, 0.3)',  // Light Blue - Faible valeur, Effort peu important
  bottomRight: 'rgba(134, 239, 172, 0.3)', // Light Green - Valeur importante, Effort modéré
};

// Assign colors based on quadrant position
const getPointColor = (x: number, y: number): string => {
  const midX = 5; // Middle of 0-10 range
  const midY = 5; // Middle of 0-10 range

  if (x < midX && y >= midY) {
    return 'hsl(24.6, 95%, 53.1%)'; // Orange - Top-left quadrant
  } else if (x >= midX && y >= midY) {
    return 'hsl(217.2, 91.2%, 59.8%)'; // Blue - Top-right quadrant
  } else if (x < midX && y < midY) {
    return 'hsl(24.6, 95%, 53.1%)'; // Orange - Bottom-left quadrant
  } else {
    return 'hsl(47.9, 95.8%, 53.1%)'; // Yellow/Gold - Bottom-right quadrant
  }
};

const UseCaseMatrixCard: React.FC<UseCaseMatrixCardProps> = ({
  title,
  externalData,
  externalDomains,
  filterDomainId,
  onUseCaseClick,
  limit = 100,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [selectedDomainId, setSelectedDomainId] = useState<string>(filterDomainId || 'all');
  const [loading, setLoading] = useState(!externalData);
  const [useCases, setUseCases] = useState<UseCaseMatrixDataPoint[]>([]);
  const [domains, setDomains] = useState<DomainOption[]>([]);
  const [zonePanel, setZonePanel] = useState<ZoneGroup | null>(null);

  // Load data if not provided externally
  useEffect(() => {
    if (externalData && externalDomains) {
      setUseCases(externalData);
      setDomains(externalDomains);
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);

        // Load use cases and domains in parallel
        const [useCasesData, domainsData] = await Promise.all([
          useCaseService.list({ limit }),
          domainService.getUserDomains(),
        ]);

        // Transform use cases to matrix data points
        const matrixData = useCasesData
          .filter((uc: UseCaseSummary) =>
            typeof uc.value_score === 'number' &&
            typeof uc.complexity_score === 'number' &&
            uc.value_score !== null &&
            uc.complexity_score !== null
          )
          .map((uc: UseCaseSummary) => ({
            id: uc.id,
            name: uc.name,
            x: uc.value_score as number,
            y: uc.complexity_score as number,
            status: uc.status,
            domainId: uc.domain_id,
          }));

        // Transform domains to options
        const domainOptions = domainsData.map((d: Domain) => ({
          id: d.id,
          name: d.name,
        }));

        setUseCases(matrixData);
        setDomains(externalDomains || domainOptions);
      } catch (error) {
        console.error('Failed to load use case matrix data', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [externalData, externalDomains, limit]);

  // Update selected domain if filterDomainId changes
  useEffect(() => {
    if (filterDomainId) {
      setSelectedDomainId(filterDomainId);
    }
  }, [filterDomainId]);

  // Get the data to use (external or fetched)
  const data = externalData || useCases;
  const domainOptions = externalDomains || domains;

  // Filter data based on selected domain
  const filteredData = useMemo(() => {
    if (selectedDomainId === 'all') {
      return data;
    }
    return data.filter(item => item.domainId === selectedDomainId);
  }, [data, selectedDomainId]);

  // Default click handler - navigate to use case
  const handleUseCaseClick = useCallback((id: string) => {
    if (onUseCaseClick) {
      onUseCaseClick(id);
    } else {
      navigate(`/cas-usage/${id}`);
    }
  }, [onUseCaseClick, navigate]);

  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Resolve a Tailwind HSL-triple CSS variable (e.g. "215 16% 47%") into a
    // canvas-usable color. Canvas fillStyle does NOT resolve `hsl(var(--x))`,
    // so we read the computed value and build the hsl() string ourselves.
    const canvasEl = chartRef.current;
    const cssVar = (name: string, fallback: string): string => {
      const v = getComputedStyle(canvasEl).getPropertyValue(name).trim();
      return v ? `hsl(${v})` : fallback;
    };
    const foregroundColor = cssVar('--foreground', 'rgb(15, 23, 42)');
    const mutedForegroundColor = cssVar('--muted-foreground', 'rgb(100, 116, 139)');
    const haloColor = cssVar('--card', 'rgba(255, 255, 255, 0.85)');

    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create background quadrants plugin
    const quadrantPlugin = {
      id: 'quadrantBackground',
      beforeDraw: (chart: ChartJS) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea || !scales.x || !scales.y) return;

        const { left, right, top, bottom } = chartArea;
        const xMid = scales.x.getPixelForValue(5);
        const yMid = scales.y.getPixelForValue(5);

        // Top-left quadrant (Faible valeur, Effort important)
        ctx.fillStyle = QUADRANT_COLORS.topLeft;
        ctx.fillRect(left, top, xMid - left, yMid - top);

        // Top-right quadrant (Valeur importante, Effort important)
        ctx.fillStyle = QUADRANT_COLORS.topRight;
        ctx.fillRect(xMid, top, right - xMid, yMid - top);

        // Bottom-left quadrant (Faible valeur, Effort peu important)
        ctx.fillStyle = QUADRANT_COLORS.bottomLeft;
        ctx.fillRect(left, yMid, xMid - left, bottom - yMid);

        // Bottom-right quadrant (Valeur importante, Effort modéré)
        ctx.fillStyle = QUADRANT_COLORS.bottomRight;
        ctx.fillRect(xMid, yMid, right - xMid, bottom - yMid);

        // Draw quadrant labels with a background-colored halo so they stay
        // legible over any colored zone in both light and dark themes.
        ctx.save();
        ctx.font = '600 11px sans-serif';
        ctx.textAlign = 'left';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 3;
        ctx.strokeStyle = haloColor;
        ctx.fillStyle = mutedForegroundColor;

        const drawLabel = (text: string, x: number, y: number) => {
          ctx.strokeText(text, x, y);
          ctx.fillText(text, x, y);
        };

        // Top-left label
        drawLabel(t('useCases.matrix.quadrantLabels.topLeft'), left + 10, top + 20);
        // Top-right label
        drawLabel(t('useCases.matrix.quadrantLabels.topRight'), xMid + 10, top + 20);
        // Bottom-left label
        drawLabel(t('useCases.matrix.quadrantLabels.bottomLeft'), left + 10, bottom - 10);
        // Bottom-right label
        drawLabel(t('useCases.matrix.quadrantLabels.bottomRight'), xMid + 10, bottom - 10);
        ctx.restore();
      },
    };

    // Register the plugin
    Chart.register(quadrantPlugin);

    // Group use cases by coordinates to handle overlapping points
    const groupedData = filteredData.reduce((acc, item) => {
      const key = `${item.x}-${item.y}`;
      if (!acc[key]) {
        acc[key] = {
          x: item.x,
          y: item.y,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    }, {} as Record<string, { x: number; y: number; items: UseCaseMatrixDataPoint[] }>);

    const groupedDataArray = Object.values(groupedData);

    const config: ChartConfiguration<'bubble'> = {
      type: 'bubble',
      data: {
        datasets: groupedDataArray.map((group) => ({
          label: group.items.map(i => i.name).join(', '),
          data: [{
            x: group.x,
            y: group.y,
            r: Math.min(8 + (group.items.length - 1) * 2, 16), // Increase size for multiple items
          }] as BubbleDataPoint[],
          backgroundColor: group.items[0].color || getPointColor(group.x, group.y),
          borderColor: 'rgba(255, 255, 255, 0.8)',
          borderWidth: 1,
          hoverRadius: 12,
          // Allow bubbles at the edges (e.g. a 10/10 use case) to draw into the
          // layout padding instead of being clipped at the chart-area border.
          clip: false,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        // Breathing room so edge bubbles (with clip:false) aren't cut by the card.
        layout: {
          padding: { top: 18, right: 18, bottom: 4, left: 4 },
        },
        scales: {
          x: {
            min: 0,
            max: 10,
            title: {
              display: true,
              text: t('useCases.matrix.axes.valueScore'),
              font: { size: 12, weight: 'bold' },
              color: foregroundColor,
            },
            grid: {
              display: true,
              color: 'rgba(148, 163, 184, 0.2)',
            },
            ticks: {
              stepSize: 1,
              color: mutedForegroundColor,
            },
          },
          y: {
            min: 0,
            max: 10,
            title: {
              display: true,
              text: t('useCases.matrix.axes.complexityScore'),
              font: { size: 12, weight: 'bold' },
              color: foregroundColor,
            },
            grid: {
              display: true,
              color: 'rgba(148, 163, 184, 0.2)',
            },
            ticks: {
              stepSize: 1,
              color: mutedForegroundColor,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            titleColor: '#ffffff',
            bodyColor: 'rgba(255, 255, 255, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderWidth: 1,
            padding: 12,
            titleFont: {
              weight: 'bold',
              size: 13,
            },
            bodyFont: {
              size: 12,
            },
            callbacks: {
              title: (context) => {
                const datasetIndex = context[0].datasetIndex;
                const group = groupedDataArray[datasetIndex];
                if (group.items.length === 1) {
                  return group.items[0].name;
                }
                return t('useCases.matrix.groupTitle', { count: group.items.length });
              },
              label: (context) => {
                const datasetIndex = context.datasetIndex;
                const group = groupedDataArray[datasetIndex];
                const lines: string[] = [];

                if (group.items.length === 1) {
                  const item = group.items[0];
                  if (item.status) {
                    lines.push(`${t('global.status')}: ${getUseCaseStatusLabel(item.status, t)}`);
                  }
                  lines.push(`${t('global.businessValue')}: ${group.x.toFixed(1)}/10`);
                  lines.push(`${t('global.technicalComplexity')}: ${group.y.toFixed(1)}/10`);
                  lines.push('');
                  lines.push(t('useCases.matrix.clickToOpen', '› Cliquer pour ouvrir la fiche'));
                } else {
                  // Multi-item bubble: list names with status
                  group.items.forEach((item, idx) => {
                    const statusLabel = item.status ? ` — ${getUseCaseStatusLabel(item.status, t)}` : '';
                    lines.push(`${idx + 1}. ${item.name}${statusLabel}`);
                  });
                  lines.push('');
                  lines.push(`${t('global.businessValue')}: ${group.x.toFixed(1)}/10  ·  ${t('global.technicalComplexity')}: ${group.y.toFixed(1)}/10`);
                  lines.push('');
                  lines.push(t('useCases.matrix.clickToList', '› Cliquer pour voir la liste'));
                }

                return lines;
              },
            },
          },
        },
        onClick: (_event, elements) => {
          if (elements.length > 0) {
            const datasetIndex = elements[0].datasetIndex;
            const group = groupedDataArray[datasetIndex];
            if (group && group.items.length === 1) {
              handleUseCaseClick(group.items[0].id);
            } else if (group && group.items.length > 1) {
              // Multiple use cases at the same coordinates: open a side panel
              // listing them so the user can pick one rather than silently
              // navigating to the first.
              setZonePanel(group);
            }
          }
        },
      },
      plugins: [quadrantPlugin],
    };

    chartInstance.current = new Chart(ctx, config);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      Chart.unregister(quadrantPlugin);
    };
  }, [filteredData, handleUseCaseClick, t]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {title ?? t('useCases.matrix.chartTitle')}
          </h3>
        </div>
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('global.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {title ?? t('useCases.matrix.chartTitle')}
          </h3>
        </div>
        <div className="h-80 flex items-center justify-center text-muted-foreground">
          {t('useCases.matrix.noScoredUseCases')}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {title ?? t('useCases.matrix.chartTitle')}
        </h3>
        {domainOptions.length > 0 && !filterDomainId && (
          <select
            value={selectedDomainId}
            onChange={(e) => setSelectedDomainId(e.target.value)}
            className="px-3 py-1.5 text-sm bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">{t('useCases.matrix.allDomains')}</option>
            {domainOptions.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {filteredData.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-muted-foreground">
          {t('useCases.matrix.noUseCasesInDomain')}
        </div>
      ) : (
        <div className="h-80">
          <canvas ref={chartRef} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: QUADRANT_COLORS.bottomRight.replace('0.3', '0.6') }}></span>
          {t('useCases.matrix.legend.quickWins')}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: QUADRANT_COLORS.topRight.replace('0.3', '0.6') }}></span>
          {t('useCases.matrix.legend.strategicProjects')}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: QUADRANT_COLORS.bottomLeft.replace('0.3', '0.6') }}></span>
          {t('useCases.matrix.legend.toEvaluate')}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: QUADRANT_COLORS.topLeft.replace('0.3', '0.6') }}></span>
          {t('useCases.matrix.legend.toAvoid')}
        </span>
      </div>

      <Sheet open={zonePanel !== null} onOpenChange={(open) => !open && setZonePanel(null)}>
        <SheetContent side="right" className="w-96 flex flex-col gap-0 p-0">
          <SheetHeader className="border-b border-border px-5 py-4">
            <SheetTitle>
              {t('useCases.matrix.zoneTitle', 'Cas d’usage de cette zone')}
            </SheetTitle>
            {zonePanel && (
              <SheetDescription>
                {t('useCases.matrix.zoneSubtitle', '{{count}} cas d’usage · Valeur {{x}}/10 · Complexité {{y}}/10', {
                  count: zonePanel.items.length,
                  x: zonePanel.x.toFixed(1),
                  y: zonePanel.y.toFixed(1),
                })}
              </SheetDescription>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {zonePanel?.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setZonePanel(null);
                  handleUseCaseClick(item.id);
                }}
                className="w-full flex items-start justify-between gap-3 rounded-md border border-transparent hover:border-border hover:bg-muted/50 px-3 py-2.5 text-left transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate" title={item.name}>
                    {item.name}
                  </p>
                  {item.status && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {getUseCaseStatusLabel(item.status, t)}
                    </p>
                  )}
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default UseCaseMatrixCard;
